import { useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, AlertCircle, X, Image as ImageIcon } from 'lucide-react';
import {
  uploadAnnouncement,
  uploadAnnouncementBatch,
  getPersonasForTarget,
  PersonaOption,
  BatchFileSpec,
} from '../lib/api/index.js';
import { listEgressTargets } from '../lib/api/egress.js';
import { listCommissions, getCommission } from '../lib/api/commissions.js';
import type { EgressTargetSummary } from '../lib/api/types.js';
import { UploadProgressCard } from '../components/UploadProgressCard.js';
import { BatchProgressCard } from '../components/BatchProgressCard.js';
import { useUploadQueue } from '../hooks/useUploadQueue.js';
import { useAuth } from '../contexts/AuthContext.js';

type SourceType =
  | 'announcement'
  | 'research_pdf'
  | 'research_markdown'
  | 'editorial_brief';

interface PendingFile {
  id: string;             // local-only id
  file: File;
  fileType: 'pdf' | 'markdown';
  sourceType: SourceType;
  bodyPassthrough: boolean;
  overrideTargetId?: string;
  overridePersonaSlug?: string;
  attachedImages: File[];
  // FT-3/FT-4 (direct-input lanes): research-source provenance + the
  // per-piece byline ruling (operator 2026-07-30).
  sourceUrl?: string;
  sourcePublisher?: string;
  bylineMode: 'house_persona' | 'human';
  humanByline?: string;
  // Editorial commissions (§7.4 d2): attach this upload to a pending part.
  commissionPartId?: string;
}

interface PendingBatchState {
  files: PendingFile[];
}

const PDF_EXTS = ['.pdf'];
const MD_EXTS = ['.md', '.markdown'];
const IMG_EXTS = ['.png', '.jpg', '.jpeg', '.webp', '.gif'];

function detectFileType(file: File): 'pdf' | 'markdown' | 'image' | 'other' {
  const name = file.name.toLowerCase();
  if (name.endsWith('.pdf') || file.type === 'application/pdf') return 'pdf';
  if (MD_EXTS.some(e => name.endsWith(e)) || file.type === 'text/markdown') return 'markdown';
  if (IMG_EXTS.some(e => name.endsWith(e)) || file.type.startsWith('image/')) return 'image';
  return 'other';
}

function defaultSourceType(fileType: 'pdf' | 'markdown'): SourceType {
  return fileType === 'markdown' ? 'editorial_brief' : 'announcement';
}

export function UploadPage() {
  const { user } = useAuth();
  const [uploadError, setUploadError] = useState<string>('');
  const [pendingBatch, setPendingBatch] = useState<PendingBatchState>({ files: [] });
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const [submittedBatches, setSubmittedBatches] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [batchLabel, setBatchLabel] = useState<string>('');
  const [batchMode, setBatchMode] = useState<'combined' | 'independent'>('combined');

  const {
    jobs,
    addJob,
    updateJob,
    removeJob,
    clearCompleted,
    clearAll,
    completedJobs,
  } = useUploadQueue();

  // Targets for body-passthrough overrides
  const [targets, setTargets] = useState<EgressTargetSummary[]>([]);
  const [personasByTarget, setPersonasByTarget] = useState<Record<string, PersonaOption[]>>({});
  // Pending commission parts an upload can attach to (§7.4 d2).
  const [attachableParts, setAttachableParts] = useState<
    Array<{ part_id: string; label: string }>
  >([]);

  useEffect(() => {
    listEgressTargets().then(r => setTargets(r.items)).catch(() => {});
    (async () => {
      try {
        const open = await listCommissions();
        const withPending = open.filter(
          c =>
            ['proposed', 'approved', 'in_progress'].includes(c.status) &&
            (c.pending_part_count ?? 0) > 0
        );
        const parts: Array<{ part_id: string; label: string }> = [];
        for (const c of withPending.slice(0, 10)) {
          const full = await getCommission(c.commission_id);
          for (const p of full.parts || []) {
            if (p.status !== 'pending') continue;
            const ent = c.entity_identifier ? `${c.entity_identifier} · ` : '';
            parts.push({
              part_id: p.part_id,
              label: `${ent}Part ${p.part_number}: ${p.working_title}`,
            });
          }
        }
        setAttachableParts(parts);
      } catch {
        /* commissions unavailable — selector simply hidden */
      }
    })();
  }, []);

  const onDrop = (accepted: File[]) => {
    setUploadError('');
    const newSources: PendingFile[] = [];
    const newImages: File[] = [];

    for (const f of accepted) {
      const kind = detectFileType(f);
      if (kind === 'image') {
        newImages.push(f);
      } else if (kind === 'pdf' || kind === 'markdown') {
        newSources.push({
          id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          file: f,
          fileType: kind,
          sourceType: defaultSourceType(kind),
          bodyPassthrough: false,
          attachedImages: [],
          bylineMode: 'house_persona',
        });
      } else {
        setUploadError(`Unsupported file type: ${f.name}`);
      }
    }

    setPendingBatch(prev => ({ files: [...prev.files, ...newSources] }));
    setPendingImages(prev => [...prev, ...newImages]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': PDF_EXTS,
      'text/markdown': MD_EXTS,
      'image/*': IMG_EXTS,
    },
    onDrop,
  });

  const isLibraryMode = pendingBatch.files.length === 0 && pendingImages.length > 0;
  const isLegacySingle =
    pendingBatch.files.length === 1 &&
    pendingBatch.files[0].fileType === 'pdf' &&
    pendingBatch.files[0].sourceType === 'announcement' &&
    !pendingBatch.files[0].bodyPassthrough &&
    pendingBatch.files[0].attachedImages.length === 0 &&
    pendingImages.length === 0;

  // Auto-switch batch mode based on file count
  useEffect(() => {
    if (pendingBatch.files.length <= 1) setBatchMode('independent');
    else setBatchMode('combined');
  }, [pendingBatch.files.length]);

  const removeSource = (id: string) => {
    setPendingBatch(prev => ({
      files: prev.files.filter(f => f.id !== id),
    }));
  };

  const updateSource = (id: string, patch: Partial<PendingFile>) => {
    setPendingBatch(prev => ({
      files: prev.files.map(f => (f.id === id ? { ...f, ...patch } : f)),
    }));
  };

  const attachImageToSource = (sourceId: string, image: File) => {
    setPendingBatch(prev => ({
      files: prev.files.map(f =>
        f.id === sourceId
          ? { ...f, attachedImages: [...f.attachedImages, image] }
          : f,
      ),
    }));
    setPendingImages(prev => prev.filter(p => p !== image));
  };

  const detachImage = (sourceId: string, imageIdx: number) => {
    setPendingBatch(prev => ({
      files: prev.files.map(f => {
        if (f.id !== sourceId) return f;
        const next = [...f.attachedImages];
        const [removed] = next.splice(imageIdx, 1);
        setPendingImages(prevImgs => [...prevImgs, removed]);
        return { ...f, attachedImages: next };
      }),
    }));
  };

  const loadPersonasForTarget = async (targetId: string) => {
    if (personasByTarget[targetId]) return;
    try {
      const res = await getPersonasForTarget(targetId);
      setPersonasByTarget(prev => ({ ...prev, [targetId]: res.personas }));
    } catch (err) {
      console.error('persona fetch failed', err);
    }
  };

  const submitLegacySingle = async () => {
    const pending = pendingBatch.files[0];
    const tempId = `temp-${Date.now()}`;
    addJob({
      id: tempId,
      fileName: pending.file.name,
      uploadedAt: new Date().toISOString(),
      status: 'uploading',
    });
    try {
      const result = await uploadAnnouncement({
        file: pending.file,
        uploadedByEmail: user?.email || 'unknown',
      });
      removeJob(tempId);
      addJob({
        id: result.announcement_id,
        fileName: pending.file.name,
        uploadedAt: new Date().toISOString(),
        status: 'processing',
      });
      setPendingBatch({ files: [] });
    } catch (err: any) {
      console.error('upload error', err);
      updateJob(tempId, { status: 'failed' });
      const detail = err.response?.data?.detail;
      const msg =
        detail && typeof detail === 'object' && detail.message
          ? detail.message
          : typeof detail === 'string'
            ? detail
            : err.response?.data?.message || err.message || 'Upload failed';
      setUploadError(msg);
    }
  };

  const submitBatch = async () => {
    setUploadError('');
    setSubmitting(true);
    try {
      // Cross-reference: every pendingImage either lives in pendingImages (orphan)
      // or in a source's attachedImages. The endpoint rejects orphans, so we only
      // submit images that are referenced.
      const referencedImages: File[] = [];
      const fileSpecs: BatchFileSpec[] = pendingBatch.files.map(pf => {
        for (const img of pf.attachedImages) referencedImages.push(img);
        return {
          filename: pf.file.name,
          source_type: pf.sourceType,
          body_passthrough: pf.bodyPassthrough || undefined,
          override_target_id: pf.overrideTargetId,
          override_persona_slug: pf.overridePersonaSlug,
          attached_images: pf.attachedImages.length
            ? pf.attachedImages.map(i => i.name)
            : undefined,
          source_url: pf.sourceUrl || undefined,
          source_publisher: pf.sourcePublisher || undefined,
          byline_mode: pf.bylineMode !== 'house_persona' ? pf.bylineMode : undefined,
          human_byline: pf.bylineMode === 'human' ? pf.humanByline : undefined,
          commission_part_id: pf.commissionPartId || undefined,
        };
      });

      const result = await uploadAnnouncementBatch({
        batch_mode: batchMode,
        uploaded_by: user?.email,
        batch_label: batchLabel || undefined,
        files: pendingBatch.files.map(p => p.file),
        fileSpecs,
        images: referencedImages,
      });

      setSubmittedBatches(prev => [...prev, result.batch_id]);
      setPendingBatch({ files: [] });
      setPendingImages([]);
      setBatchLabel('');
    } catch (err: any) {
      console.error('batch upload error', err);
      const detail = err.response?.data?.detail;
      const msg =
        detail && typeof detail === 'object' && detail.message
          ? detail.message
          : typeof detail === 'string'
            ? detail
            : err.response?.data?.message || err.message || 'Batch upload failed';
      setUploadError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const submitLibrary = async () => {
    setUploadError('');
    setSubmitting(true);
    try {
      const formData = new FormData();
      for (const img of pendingImages) formData.append('images', img, img.name);
      const res = await fetch('/images/library', {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Library upload failed: ${res.status} ${text}`);
      }
      setPendingImages([]);
    } catch (err: any) {
      console.error('library upload error', err);
      setUploadError(err.message || 'Library upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  const hasPending = pendingBatch.files.length > 0 || pendingImages.length > 0;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Direct-input guidance (operator feedback 2026-07-31): what each
          submission shape achieves, with the template downloadable from the
          page itself — not hidden behind adding a research file. */}
      <div className="bg-surface rounded-xl shadow-md border border-grey-200 p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-grey-900 mb-1">What you can submit</h2>
            <ul className="text-sm text-grey-700 space-y-1.5 list-disc pl-5">
              <li>
                <span className="font-semibold">Announcement PDFs</span> — the standard
                pipeline: extract, classify, generate, quality-gate, route by the network's
                ownership rules.
              </li>
              <li>
                <span className="font-semibold">Research reports</span> (PDF or markdown,
                authored to the template) — the report becomes the grounded source of a
                generated article: every claim traces to your referenced facts, the report
                is named and linked as the article's cited source, and the truth-check
                anchors to it.
              </li>
              <li>
                <span className="font-semibold">Editorial briefs</span> (markdown, "body
                passthrough") — publish your exact words: the text is used verbatim as the
                article body, judged by the same quality gate as generated content, and
                never silently rewritten.
              </li>
              <li>
                <span className="font-semibold">Byline per piece</span> — your name with an
                AI-assistance disclosure, or a house persona with a human-validation
                disclosure. Choose when you upload.
              </li>
            </ul>
            <p className="text-xs text-grey-500 mt-2">
              Every submission passes the same blocking quality gate as generated content —
              a held piece appears in the Review Queue with the reason. Indexation is earned
              by the editorial spec, never granted.
            </p>
          </div>
          <a
            href="/research-report-template.md"
            download="research-report-template.md"
            className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-primary-200 bg-primary-50 text-primary-700 text-sm font-semibold hover:bg-primary-100"
          >
            Download the research-report template
          </a>
        </div>
      </div>

      {/* Upload dropzone */}
      <div className="bg-surface rounded-xl shadow-md border border-grey-200 p-8">
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-primary-100 bg-primary-50 bg-opacity-10'
              : 'border-grey-200 hover:border-primary-100'
          }`}
        >
          <input {...getInputProps()} />
          <Upload className="h-16 w-16 text-primary-600 mx-auto mb-4" />
          <p className="text-grey-900 font-bold text-xl mb-2">
            {isDragActive ? 'Drop files here…' : 'Drag & drop PDFs, markdown, or images'}
          </p>
          <p className="text-grey-600 font-medium mb-1">or click to select files</p>
          <p className="text-sm text-grey-500">
            PDFs ≤50MB · Markdown ≤2MB · Images ≤10MB · Mix freely
          </p>
        </div>

        {uploadError && (
          <div className="mt-4 border-2 border-red-200 bg-red-50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-red-900 mb-1">Upload failed</h3>
                <p className="text-sm text-red-700">{uploadError}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pending batch panel */}
      {hasPending && (
        <div className="bg-surface rounded-xl shadow-md border border-grey-200 p-6 space-y-4">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-lg font-bold text-grey-900">
              {isLibraryMode ? 'Library upload' : 'Pending batch'}
            </h2>
            {!isLibraryMode && (
              <div className="flex items-center gap-3 text-sm">
                <label className="flex items-center gap-1 text-grey-700">
                  <input
                    type="radio"
                    name="batchMode"
                    value="independent"
                    checked={batchMode === 'independent'}
                    onChange={() => setBatchMode('independent')}
                  />
                  Independent
                </label>
                <label className="flex items-center gap-1 text-grey-700">
                  <input
                    type="radio"
                    name="batchMode"
                    value="combined"
                    checked={batchMode === 'combined'}
                    onChange={() => setBatchMode('combined')}
                    disabled={pendingBatch.files.length < 2}
                  />
                  Combined
                </label>
              </div>
            )}
          </div>

          {!isLibraryMode && (
            <input
              type="text"
              placeholder="Batch label (optional)"
              value={batchLabel}
              onChange={e => setBatchLabel(e.target.value)}
              className="w-full px-3 py-2 border border-grey-200 rounded text-sm"
            />
          )}

          {/* Source documents */}
          {pendingBatch.files.length > 0 && (
            <ul className="space-y-3">
              {pendingBatch.files.map(pf => (
                <li
                  key={pf.id}
                  className="border border-grey-200 rounded p-3 space-y-2"
                >
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-grey-400" />
                    <span className="flex-1 truncate font-medium text-grey-900">
                      {pf.file.name}
                    </span>
                    <select
                      value={pf.sourceType}
                      onChange={e => {
                        const st = e.target.value as SourceType;
                        // Commissioned-report default (operator 2026-08-05):
                        // most research uploads are reports WE commissioned,
                        // so the report-source field seeds to the house
                        // entity when switching into a research class.
                        // Cleared or edited freely for third-party reports.
                        const isResearch = st === 'research_pdf' || st === 'research_markdown';
                        updateSource(pf.id, {
                          sourceType: st,
                          ...(isResearch && !pf.sourcePublisher
                            ? { sourcePublisher: 'Vyklow Analytics Pty Ltd' }
                            : {}),
                        });
                      }}
                      className="text-sm border border-grey-200 rounded px-2 py-1"
                    >
                      <option value="announcement">announcement</option>
                      {/* Only offer the research variant matching the actual
                          file — a .md submitted as research_pdf took the wrong
                          extraction identity (2026-08-03). Backend coerces as
                          a second line of defence. */}
                      {pf.fileType === 'pdf' ? (
                        <option value="research_pdf">research (PDF)</option>
                      ) : (
                        <option value="research_markdown">research (markdown)</option>
                      )}
                      <option value="editorial_brief">editorial brief</option>
                    </select>
                    <button
                      onClick={() => removeSource(pf.id)}
                      className="text-grey-500 hover:text-red-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  {pf.sourceType === 'editorial_brief' && (
                    <div className="pl-6 space-y-2">
                      <label className="flex items-center gap-2 text-sm text-grey-700">
                        <input
                          type="checkbox"
                          checked={pf.bodyPassthrough}
                          onChange={e =>
                            updateSource(pf.id, { bodyPassthrough: e.target.checked })
                          }
                        />
                        Body passthrough (use markdown verbatim)
                      </label>
                      {pf.bodyPassthrough && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <select
                            value={pf.overrideTargetId || ''}
                            onChange={async e => {
                              const t = e.target.value || undefined;
                              updateSource(pf.id, {
                                overrideTargetId: t,
                                overridePersonaSlug: undefined,
                              });
                              if (t) await loadPersonasForTarget(t);
                            }}
                            className="text-sm border border-grey-200 rounded px-2 py-1"
                          >
                            <option value="">Choose site…</option>
                            {targets.map(t => (
                              <option key={t.target_id} value={t.target_id}>
                                {t.target_name}
                              </option>
                            ))}
                          </select>
                          <select
                            value={pf.overridePersonaSlug || ''}
                            onChange={e =>
                              updateSource(pf.id, {
                                overridePersonaSlug: e.target.value || undefined,
                              })
                            }
                            disabled={!pf.overrideTargetId}
                            className="text-sm border border-grey-200 rounded px-2 py-1"
                          >
                            <option value="">Choose persona…</option>
                            {(pf.overrideTargetId &&
                              personasByTarget[pf.overrideTargetId] || []).map(p => (
                              <option key={p.slug} value={p.slug}>
                                {p.display_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Research classes REQUIRE an owning site (backend R1:
                      one report → one article → one masthead). This picker
                      previously rendered only for editorial_brief +
                      body-passthrough, so research uploads could never
                      satisfy the validation — the operator filled the
                      Publisher box instead and got the R1 error
                      (2026-08-05). */}
                  {(pf.sourceType === 'research_pdf' || pf.sourceType === 'research_markdown') && (
                    <div className="pl-6 space-y-1">
                      <select
                        value={pf.overrideTargetId || ''}
                        onChange={async e => {
                          const t = e.target.value || undefined;
                          updateSource(pf.id, {
                            overrideTargetId: t,
                            overridePersonaSlug: undefined,
                          });
                          if (t) await loadPersonasForTarget(t);
                        }}
                        className={`text-sm border rounded px-2 py-1 ${pf.overrideTargetId ? 'border-grey-200' : 'border-warning-400 bg-warning-50'}`}
                      >
                        <option value="">Target site (required)…</option>
                        {targets.map(t => (
                          <option key={t.target_id} value={t.target_id}>
                            {t.target_name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-grey-500">
                        One report → one article → one masthead. The report's stream decides it:
                        Orecast = production economics · Bell Wire = market structure · Market Floor = economy-wide flow-through.
                      </p>
                    </div>
                  )}

                  {attachableParts.length > 0 && (
                    <div className="pl-6 flex items-center gap-2">
                      <span className="text-xs text-grey-500">
                        Attach to commission part:
                      </span>
                      <select
                        value={pf.commissionPartId || ''}
                        onChange={e =>
                          updateSource(pf.id, {
                            commissionPartId: e.target.value || undefined,
                          })
                        }
                        className="text-sm border border-grey-200 rounded px-2 py-1 max-w-md"
                      >
                        <option value="">None</option>
                        {attachableParts.map(p => (
                          <option key={p.part_id} value={p.part_id}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {(pf.sourceType === 'research_pdf' || pf.sourceType === 'research_markdown') && (
                    <div className="pl-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input
                        type="url"
                        placeholder="Source URL (report link — feeds isBasedOn)"
                        value={pf.sourceUrl || ''}
                        onChange={e => updateSource(pf.id, { sourceUrl: e.target.value || undefined })}
                        className="text-sm border border-grey-200 rounded px-2 py-1"
                      />
                      <input
                        type="text"
                        placeholder="Report source (who published the document)"
                        title="The organisation that authored/published the uploaded report — feeds the article's isBasedOn attribution, NOT the masthead it publishes on (that's Target site below). Reports we commissioned: Vyklow Analytics Pty Ltd."
                        value={pf.sourcePublisher || ''}
                        onChange={e => updateSource(pf.id, { sourcePublisher: e.target.value || undefined })}
                        className="text-sm border border-grey-200 rounded px-2 py-1"
                      />
                      <a
                        href="/research-report-template.md"
                        download="research-report-template.md"
                        className="col-span-full text-xs text-primary-600 underline"
                      >
                        Download the research-report template (.md) — author to it for
                        claim-by-claim grounding
                      </a>
                    </div>
                  )}

                  {pf.sourceType !== 'announcement' && (
                    <div className="pl-6 flex flex-wrap items-center gap-2 text-sm text-grey-700">
                      <span className="font-medium">Byline:</span>
                      <select
                        value={pf.bylineMode}
                        onChange={e => updateSource(pf.id, { bylineMode: e.target.value as 'house_persona' | 'human' })}
                        className="text-sm border border-grey-200 rounded px-2 py-1"
                      >
                        <option value="house_persona">House persona (human-validated disclosure)</option>
                        <option value="human">Journalist byline (AI-assistance disclosure)</option>
                      </select>
                      {pf.bylineMode === 'human' && (
                        <input
                          type="text"
                          placeholder="Journalist name"
                          value={pf.humanByline || ''}
                          onChange={e => updateSource(pf.id, { humanByline: e.target.value || undefined })}
                          className="text-sm border border-grey-200 rounded px-2 py-1"
                        />
                      )}
                    </div>
                  )}

                  {/* Attached images for this source */}
                  {pf.attachedImages.length > 0 && (
                    <ul className="pl-6 space-y-1">
                      {pf.attachedImages.map((img, idx) => (
                        <li
                          key={`${pf.id}-${img.name}-${idx}`}
                          className="flex items-center gap-2 text-xs text-grey-700"
                        >
                          <ImageIcon className="h-3 w-3 text-blue-500" />
                          <span className="flex-1 truncate">{img.name}</span>
                          <button
                            onClick={() => detachImage(pf.id, idx)}
                            className="text-grey-500 hover:text-red-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Attach-image buttons for pending orphan images */}
                  {pendingImages.length > 0 && (
                    <div className="pl-6">
                      <p className="text-xs text-grey-500 mb-1">Attach image:</p>
                      <div className="flex flex-wrap gap-2">
                        {pendingImages.map(img => (
                          <button
                            key={img.name}
                            onClick={() => attachImageToSource(pf.id, img)}
                            className="text-xs border border-grey-200 rounded px-2 py-0.5 hover:bg-grey-50"
                          >
                            + {img.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* Library-mode image list */}
          {isLibraryMode && (
            <ul className="space-y-1">
              {pendingImages.map((img, idx) => (
                <li
                  key={`lib-${img.name}-${idx}`}
                  className="flex items-center gap-2 text-sm text-grey-700"
                >
                  <ImageIcon className="h-4 w-4 text-blue-500" />
                  <span className="flex-1 truncate">{img.name}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Submit */}
          <div className="flex items-center gap-3 pt-2">
            {isLibraryMode ? (
              <button
                onClick={submitLibrary}
                disabled={submitting || pendingImages.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded font-medium disabled:opacity-50"
              >
                {submitting ? 'Uploading…' : `Submit ${pendingImages.length} to library`}
              </button>
            ) : isLegacySingle ? (
              <button
                onClick={submitLegacySingle}
                disabled={submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded font-medium disabled:opacity-50"
              >
                {submitting ? 'Uploading…' : 'Upload announcement'}
              </button>
            ) : (
              <button
                onClick={submitBatch}
                disabled={submitting || pendingBatch.files.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded font-medium disabled:opacity-50"
              >
                {submitting
                  ? 'Submitting…'
                  : `Submit batch (${pendingBatch.files.length} source${pendingBatch.files.length === 1 ? '' : 's'})`}
              </button>
            )}
            <button
              onClick={() => {
                setPendingBatch({ files: [] });
                setPendingImages([]);
                setBatchLabel('');
              }}
              className="text-sm text-grey-600 hover:text-grey-900 font-medium"
            >
              Clear pending
            </button>
          </div>
        </div>
      )}

      {/* Submitted batches */}
      {submittedBatches.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-bold text-grey-900">Active batches</h2>
          {submittedBatches.map(bid => (
            <BatchProgressCard
              key={bid}
              batchId={bid}
              onClose={() =>
                setSubmittedBatches(prev => prev.filter(x => x !== bid))
              }
            />
          ))}
        </div>
      )}

      {/* Legacy single-file processing queue */}
      {jobs.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-grey-900">
              Processing Queue ({jobs.length})
            </h2>
            <div className="flex items-center gap-2">
              {completedJobs.length > 0 && (
                <button
                  onClick={clearCompleted}
                  className="text-sm text-grey-600 hover:text-grey-900 font-semibold"
                >
                  Clear Completed
                </button>
              )}
              <button
                onClick={clearAll}
                className="text-sm text-red-600 hover:text-red-700 font-semibold"
              >
                Clear All
              </button>
            </div>
          </div>

          {jobs.map(job => (
            <div key={job.id}>
              {job.id.startsWith('temp-') ? (
                <div
                  className={`bg-surface rounded-lg shadow-sm border p-6 ${
                    job.status === 'failed' ? 'border-red-200' : 'border-grey-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {job.status === 'failed' ? (
                      <AlertCircle className="h-5 w-5 text-red-500" />
                    ) : (
                      <Upload className="h-5 w-5 text-blue-600 animate-pulse" />
                    )}
                    <div className="flex-1">
                      <h3 className="font-semibold text-grey-900">
                        {job.status === 'failed' ? 'Upload Failed' : 'Uploading…'}
                      </h3>
                      <p className="text-sm text-grey-600">{job.fileName}</p>
                    </div>
                    {job.status === 'failed' && (
                      <button
                        onClick={() => removeJob(job.id)}
                        className="text-sm text-grey-500 hover:text-grey-700 font-medium"
                      >
                        Dismiss
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <UploadProgressCard
                  announcementId={job.id}
                  onStatusChange={status => {
                    const jobStatus = ['completed', 'failed'].includes(status)
                      ? (status as 'completed' | 'failed')
                      : 'processing';
                    updateJob(job.id, { status: jobStatus });
                  }}
                  onComplete={() => updateJob(job.id, { status: 'completed' })}
                  onClose={() => removeJob(job.id)}
                  onError={() => updateJob(job.id, { status: 'failed' })}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {jobs.length === 0 && submittedBatches.length === 0 && !hasPending && (
        <div className="bg-surface rounded-xl shadow-md border border-grey-200 p-16 text-center">
          <FileText className="h-16 w-16 text-grey-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-grey-900 mb-2">No Active Uploads</h2>
          <p className="text-base text-grey-600 mb-1">
            Drop PDFs, markdown briefs, or images to get started
          </p>
          <p className="text-sm text-grey-500">
            Single PDFs use the legacy flow. Multi-file uploads go through the batch path.
          </p>
        </div>
      )}
    </div>
  );
}
